/**
 * This file defines the Login component for the React application.
 *
 * This page will hold the form for users to log in to the application.
 *
 * Author: Jennifer Tran, Meng Li
 * Created on: 2024-10-28
 * Professor: Mejdi Eraslan
 * Client: Melisa Eraslan
 */

import React, { useState } from 'react';
import '../../assets/css/Login.css';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebaseConfig';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    // Handle user login
    const handleLogin = async (event) => {
        event.preventDefault();

        try {
            // Sign in the user with email and password
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Get user UID and retrieve user document from Firestore
            const uid = user.uid;
            const userDoc = await getDoc(doc(db, 'users', uid));

            if (userDoc.exists()) {
                const { related_entity_number } = userDoc.data();

                // Retrieve the entity document based on the related entity number
                const entityDoc = await getDoc(doc(db, 'entities', related_entity_number.toString()));
                if (entityDoc.exists()) {
                    const { entity_type, entity_name } = entityDoc.data();

                    // Check if entity name is SimpliCITY
                    if (entity_name === 'SimpliCITY') {
                        navigate('/accountDetails', {
                            state: { entityName: entity_name, showAdminOptions: true }
                        });
                        return;
                    } else {
                        navigate('/accountDetails', {
                            state: { entityName: entity_name, showAdminOptions: false }
                        });
                        return;
                    }
                } else {
                    setError('The specified entity could not be found.');
                }
            } else {
                setError('User not found. Please check your login details.');
            }
        } catch (error) {
            // Replace technical error messages with more user-friendly messages
            if (error.code === 'auth/invalid-credential') {
                setError('Incorrect email or password. Please try again.');
            } else {
                setError('An unexpected error occurred. Please try again later.');
            }
        }
    };

    const closeModal = () => {
        setError(null);
    };

    return (
        <div className="login-page">
            <div className="login-container">
                <p className="title-text">LOG IN</p>
                <form onSubmit={handleLogin}>
                    <label htmlFor="email" className="field-label">Email</label>
                    <input
                        type="email"
                        id="email"
                        placeholder="Email@example.com"
                        className="input-field"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />

                    <label htmlFor="password" className="field-label">Password</label>
                    <input
                        type="password"
                        id="password"
                        placeholder="Enter your password"
                        className="input-field"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    <button type="submit" className="continue-btn">Continue</button>
                </form>
                <p className="info-text">
                    If you are a shelter staff member and would like to join, please
                    <Link to="/contact" className="contact-link"> contact us.</Link>
                </p>
            </div>

            {/* Error Modal */}
            {error && (
                <div className="modal" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <p>{error}</p>
                        <button onClick={closeModal} className="close-btn">Close</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Login;
